import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";

// The two writes behind the refunds table's Action column.
//
// Both are v2-specific rather than reusing src/hooks/update-hooks/issue-refund, which
// cannot be used here for two reasons:
//
//  1. It invalidates the LEGACY query keys (get-all-refunds), so the v2 table would keep
//     showing the pre-action row until a manual refresh.
//  2. Its onError reads error.response, but the shared request() helper RESOLVES on its
//     error branch instead of throwing, and pillup-api returns business failures as
//     {success:false} at HTTP 200. So a refused action -- "this refund is not APPROVED"
//     -- would have shown a success toast. That matters most here: the whole point of
//     the gate is that it can refuse.
//
// Both gates live on the server (approveRefund requires REQUESTED, issueRefundToPartner
// requires APPROVED). These hooks surface the refusal; they do not enforce it.

const runRefundAction = async (url) => {
    const result = await request({ url, method: "POST" });
    throwIfRequestError(result);
    const body = result.data;
    if (!body?.success) throw body;
    return body.data;
};

/** Invalidate every refunds list, whichever console is showing one. */
const invalidateRefunds = (queryClient) => {
    queryClient.invalidateQueries(["finance-refunds"]);
    // The legacy screens read these; a refund approved here must not look pending there.
    queryClient.invalidateQueries(["get-all-refunds"]);
    queryClient.invalidateQueries(["get-refunds-by-order-id"]);
};

/** POST /api/payment/approveRefund/:id — REQUESTED -> APPROVED. ADMIN / SUPER_ADMIN only. */
export function useApproveRefund() {
    const queryClient = useQueryClient();

    return useMutation((refundId) => runRefundAction(`/payment/approveRefund/${refundId}`), {
        onSuccess: () => toast.success("Refund approved."),
        onError: (error) => {
            // A refund that is no longer REQUESTED (someone approved it first) comes back
            // as refund_not_approvable. Naming it beats a generic failure, because the
            // row the user is looking at is simply stale.
            toast.error(error?.error || "Could not approve this refund. It may already have been approved.");
        },
        onSettled: () => invalidateRefunds(queryClient),
    });
}

/** POST /api/payment/issueRefundById/:id — APPROVED -> gateway. Refuses anything else. */
export function useIssueRefund() {
    const queryClient = useQueryClient();

    return useMutation((refundId) => runRefundAction(`/payment/issueRefundById/${refundId}`), {
        onSuccess: () => toast.success("Refund sent to the payment gateway."),
        onError: (error) => {
            toast.error(error?.error || "Could not initiate this refund. It must be approved first.");
        },
        onSettled: () => invalidateRefunds(queryClient),
    });
}
